import { isAudioFile } from '@lib/utils/fileExtensions';
import { Media, MediaStatus } from '@typings/Media.js';

const DEBUG_CARD_STATUSES = [
  {
    id: 'uploading',
    label: 'Uploading 45%',
    status: 'uploading' as MediaStatus,
    progress: 45,
    capabilities: { search: false, preview: false },
  },
  {
    id: 'queued',
    label: 'Queued',
    status: 'queued' as MediaStatus,
    progress: 0,
    capabilities: { search: false, preview: false },
  },
  {
    id: 'processing-no-video',
    label: 'Processing (no video)',
    status: 'processing' as MediaStatus,
    progress: 0.25,
    capabilities: { search: false, preview: false },
  },
  {
    id: 'processing-with-video',
    label: 'Processing (with video)',
    status: 'processing' as MediaStatus,
    progress: 0.75,
    capabilities: { search: false, preview: true },
  },
  {
    id: 'errored',
    label: 'Errored',
    status: 'errored' as MediaStatus,
    progress: 0,
    capabilities: { search: false, preview: false },
  },
  {
    id: 'completed',
    label: 'Completed',
    status: 'completed' as MediaStatus,
    progress: 1,
    capabilities: { search: true, preview: true },
  },
];

const DEBUG_CARD_CONTENT_TYPES = [
  { id: 'video', label: 'Video', filename: null }, // keep original filename
  { id: 'audio', label: 'Audio', filename: 'debug.mp3' },
];

const DEBUG_CARD_ORIENTATIONS = [
  { id: 'landscape', label: 'Landscape', width: 1920, height: 1080 },
  { id: 'portrait', label: 'Portrait', width: 1080, height: 1920 },
  { id: 'square', label: 'Square', width: 1080, height: 1080 },
];

export type DebugMediaCard = Media & { debugKey: string };

/**
 * Generate debug media cards covering realistic combinations of
 * status × content type × orientation.
 *
 * Not every combination is valid:
 * - Audio has no video dimensions, so orientation variants are skipped (one card per status).
 * - Audio can't have a video preview, so "processing-with-video" is skipped for audio.
 *
 * Uses real completed media items as templates, cycling through them.
 * Keeps the real _id so API calls (HLS, thumbnails) still work.
 * Each card gets a unique `debugKey` for use as the masonry cell key.
 */
export function createDebugMediaCardFiles(completedMedia: Media[]): DebugMediaCard[] {
  if (completedMedia.length === 0) return [];

  const result: DebugMediaCard[] = [];
  let index = 0;

  for (const statusConfig of DEBUG_CARD_STATUSES) {
    for (const contentType of DEBUG_CARD_CONTENT_TYPES) {
      const isAudio = contentType.id === 'audio';

      // Audio can't have a video preview — skip processing-with-video
      if (isAudio && statusConfig.id === 'processing-with-video') continue;

      // Audio has no video dimensions — only produce one card (no orientation variants).
      // width/height must be 0 so isAudioMedia() correctly identifies them as audio.
      const orientations = isAudio
        ? [{ id: 'default', label: 'Audio', width: 0, height: 0 }]
        : DEBUG_CARD_ORIENTATIONS;

      for (const orientation of orientations) {
        const template = completedMedia[index % completedMedia.length];
        const rawOriginalName = template.metadata?.name || template.metadata?.filename || 'Media';
        // Templates cycle through all completed media including audio files.
        // For video cards, strip audio extensions so isAudioFile(alt) in Thumbnail
        // doesn't incorrectly show an audio icon on what's supposed to be a video card.
        const originalName =
          !isAudio && isAudioFile(rawOriginalName)
            ? rawOriginalName.replace(/\.[^.]+$/, '.mp4')
            : rawOriginalName;
        const rawFilename = template.metadata?.filename ?? 'debug.mp4';
        const filename =
          contentType.filename ??
          (!isAudio && isAudioFile(rawFilename)
            ? rawFilename.replace(/\.[^.]+$/, '.mp4')
            : rawFilename);
        // For audio cards, the name must end with an audio extension so isAudioFile(alt) matches
        // in the Thumbnail component (which uses metadata.name as alt text).
        const displayName = contentType.filename
          ? `[${statusConfig.label} | ${contentType.label}] ${contentType.filename}`
          : `[${statusConfig.label} | ${contentType.label} | ${orientation.label}] ${originalName}`;

        result.push({
          ...template,
          // Audio files don't have userMetadata thumbnails (those come from YouTube imports).
          // Clear it so the template's thumbnail doesn't bleed through.
          ...(isAudio ? { userMetadata: {} } : {}),
          debugKey: `debug-card-${statusConfig.id}-${contentType.id}-${orientation.id}-${template._id}`,
          status: statusConfig.status,
          progress: statusConfig.progress,
          capabilities: statusConfig.capabilities,
          metadata: {
            ...template.metadata,
            name: displayName,
            filename,
            video: {
              ...(template.metadata?.video ?? {
                duration: 0,
                frameCount: 0,
                originalMediaSize: 0,
                previewSize: 0,
              }),
              width: orientation.width,
              height: orientation.height,
            },
            fileMappings: template.metadata?.fileMappings ?? {},
          },
        });
        index++;
      }
    }
  }

  // Deterministic shuffle so cards of the same status aren't grouped together.
  // Uses a simple hash of the debugKey for stable ordering across renders.
  const hash = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    }
    return h;
  };
  result.sort((a, b) => hash(a.debugKey) - hash(b.debugKey));

  return result;
}
